// MagicLinkLanding (NEW DESIGN — visual revamp only). Logic verbatim copy of
// MagicLinkLanding.js. Differences:
//   - LoginNewWrapper instead of LoginWrapper.
//   - Navigations use canonical /login (the new design now lives at /login).
//   - Status (loading / wrong-browser / generic-error) blocks reuse the new design.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Row, Col, Result } from 'antd';
import { ThemeButton } from '../Styles/Theme';
import { LoginNewWrapper } from './LoginNewStyle';
import LoginLeftPanel from './LoginLeftPanel';
import LottieLoader from '../../Components/common/LottieLoader';
import { consumeMagicLinkApi, loginReset } from '../../store/actions/loginAction';
import { setPermissions } from '../../store/actions/permissionsAction';
import { STORAGE_KEYS } from '../../utils/constants';
import { checkModulePermissions, getFirstAvailableRoute, isLogin } from '../../utils/helpers';

const MagicLinkLandingNew = (props) => {
	const { loginState, consume, loginReset, setPermissions } = props;
	const { token } = useParams();
	const navigate = useNavigate();
	const [status, setStatus] = useState('loading');
	const [errorMsg, setErrorMsg] = useState('');
	const [errorKind, setErrorKind] = useState('generic');

	useEffect(() => {
		if (!token) {
			setStatus('error');
			setErrorKind('generic');
			setErrorMsg('Missing token in link.');
			return;
		}

		const lastConsumed = localStorage.getItem('mfaLastConsumedToken');
		if (lastConsumed === token && isLogin()) {
			navigate('/', { replace: true });
			return;
		}

		const mfaSessionToken = localStorage.getItem('mfaSessionToken');

		if (!mfaSessionToken) {
			setStatus('error');
			setErrorKind('wrong-browser');
			setErrorMsg('');
			return;
		}

		consume({ token, mfaSessionToken });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	useEffect(() => {
		if (loginState.apiState === 'success') {
			if (loginState.data) {
				localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, loginState.data.accessToken);
				localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, loginState.data.refreshToken);
				localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(loginState.data.user));
				if (loginState.data.permissions) {
					localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(loginState.data.permissions));
					setPermissions(loginState.data.permissions);
				}
				if (loginState.data.appVersion) {
					localStorage.setItem('app_version', loginState.data.appVersion);
				}
			}
			if (token) {
				localStorage.setItem('mfaLastConsumedToken', token);
			}
			localStorage.removeItem('mfaSessionToken');
			localStorage.removeItem('mfaMaskedEmail');
			localStorage.removeItem('mfaExpiryMinutes');
			loginReset();
			// Per-org login redirect: backend sets redirect_chat_id when the
			// user's org has chat_redirection_enabled. Take that path before
			// the normal landing-page fallback so the user lands directly in
			// their fresh "New Insights" chat.
			if (loginState.data?.redirect_chat_id) {
				navigate(`/insight-engine/${loginState.data.redirect_chat_id}`);
				return;
			}
			const dashboardPermission = checkModulePermissions('dashboard');
			if (dashboardPermission.authorized) {
				navigate('/', { replace: true });
			} else {
				const first = getFirstAvailableRoute();
				navigate(first ? first.path : '/', { replace: true });
			}
		} else if (loginState.apiState === 'error') {
			const code = loginState.data?.data?.code;
			let friendlyMessage;
			if (code === 'MFA_LINK_EXPIRED') {
				friendlyMessage = 'This sign-in link has expired. Please sign in again to receive a fresh one.';
			} else if (code === 'MFA_LINK_INVALIDATED' || code === 'MFA_SESSION_INVALID') {
				friendlyMessage = 'This link is no longer valid. Whenever you tap "Resend link", older emails stop working - please open the link from the most recent email, or sign in again.';
			} else {
				friendlyMessage = 'This sign-in link is invalid or has already been used. Please sign in again.';
			}
			setStatus('error');
			setErrorMsg(friendlyMessage);
			loginReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loginState]);

	if (status === 'error') {
		if (errorKind === 'wrong-browser') {
			return (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: '100vh',
						padding: '20px',
						backgroundColor: '#f5f5f5',
					}}
				>
					<Result
						status='warning'
						title='Open this link in the right browser'
						subTitle={
							<div style={{ maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>
								Please open this sign-in link in the <strong>same browser</strong> where
								you are trying to sign in.
							</div>
						}
					/>
				</div>
			);
		}

		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
					padding: '20px',
					backgroundColor: '#f5f5f5',
				}}
			>
				<Result
					status='error'
					title='Sign-in link unavailable'
					subTitle={errorMsg}
					extra={
						<ThemeButton size='large' className='solidPurple' onClick={() => navigate('/login', { replace: true })}>
							Back to sign in
						</ThemeButton>
					}
				/>
			</div>
		);
	}

	return (
		<LoginNewWrapper>
			<Row gutter={[20, 16]}>
				<Col
					xs={{ span: 24, order: 1 }}
					sm={{ span: 24, order: 1 }}
					md={{ span: 12, order: 2 }}
					lg={{ span: 14, order: 2 }}
					xl={{ span: 14, order: 2 }}
				>
					<div className='loginRightCard'>
						<div className='loginLogo'>
							<img src='/images/login-new/crowdvox-logo.svg' alt='CrowdVox' />
						</div>

						<div className='loginContainer'>
							<div className='loginStatusBlock'>
								<LottieLoader inline size='medium' />
								<h1>Signing you in...</h1>
								<p>Please wait a moment.</p>
							</div>
						</div>

						<div className='loginFooter'>
							<span>@{new Date().getFullYear()} CrowdVox Inc.</span>
						</div>
					</div>
				</Col>

				<Col
					xs={{ span: 0, order: 2 }}
					sm={{ span: 0, order: 2 }}
					md={{ span: 12, order: 1 }}
					lg={{ span: 10, order: 1 }}
					xl={{ span: 10, order: 1 }}
				>
					<LoginLeftPanel />
				</Col>
			</Row>
		</LoginNewWrapper>
	);
};

const mapStateToProps = (state) => ({ loginState: state.login });
const mapDispatchToProps = (dispatch) => ({
	consume: (params) => dispatch(consumeMagicLinkApi(params)),
	loginReset: () => dispatch(loginReset()),
	setPermissions: (params) => dispatch(setPermissions(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MagicLinkLandingNew);
